import { useState, useEffect } from "react";
import { CISA_SECTORS, SECTOR_EMOJI } from "../utils/sectors";
import { levelRank } from "../utils/prevalence";
import { fmtTs } from "../utils/time";

function LevelBadge({ level, small, tooltip }) {
  if (!level) return <span className="jobs-muted">--</span>;
  const cls = `products-badge${small ? " products-badge-sm" : ""} products-badge-${level}`;
  if (!tooltip) return <span className={cls}>{level}</span>;
  return (
    <span className={`${cls} products-tooltip`} data-tooltip={tooltip}>
      {level}
    </span>
  );
}

function driverName(driver) {
  if (!driver) return "";
  return [driver.vendor, driver.product].filter(Boolean).join(" ");
}

// Sector levels sorted worst-first: the reader wants the sectors this CVE is
// most present in, not an alphabetical tour of all sixteen.
function sortedSectors(sectors) {
  return Object.entries(sectors || {}).sort(
    (a, b) => levelRank(b[1]?.level ?? b[1]) - levelRank(a[1]?.level ?? a[1]),
  );
}

// ── Raw JSON ─────────────────────────────────────────────────────────────

// The dashboard is served over plain http on a private address, so
// navigator.clipboard is undefined outside a secure context — the modern API
// alone would leave the button dead on the deployment that actually runs.
// Falls back to the execCommand path, which still works there.
async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the textarea path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

function JsonModal({ data, cve, onClose }) {
  const [copied, setCopied] = useState(null);
  const text = JSON.stringify(data, null, 2);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const lines = text.split("\n").length;
  const kb = (new Blob([text]).size / 1024).toFixed(1);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "var(--bg-primary)", borderRadius: 8, border: "1px solid var(--border)",
        width: "min(1000px, 94vw)", maxHeight: "88vh", overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{
          padding: "14px 20px", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>
            Prevalence JSON{cve ? ` — ${cve}` : ""}
          </span>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
            {lines.toLocaleString()} lines · {kb} KB
          </span>
          <button
            className="raw-json-link"
            style={{ marginLeft: "auto" }}
            onClick={async () => {
              const ok = await copyText(text);
              setCopied(ok ? "Copied" : "Copy failed");
              setTimeout(() => setCopied(null), 1800);
            }}
          >
            {copied || "Copy"}
          </button>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 18, color: "var(--text-secondary)", padding: "0 4px",
            }}
          >{"\u2715"}</button>
        </div>
        <div style={{ overflow: "auto", padding: 16 }}>
          <pre style={{
            margin: 0, fontSize: 11.5, lineHeight: 1.5,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            color: "var(--text-secondary)", whiteSpace: "pre", tabSize: 2,
          }}>{text}</pre>
        </div>
      </div>
    </div>
  );
}

// ── CVE-level rollup ─────────────────────────────────────────────────────
// A CVE is as exposed as its most widely deployed affected product, so the
// backend rolls the per-product assessments up with a max — per sector as
// well as globally — and names the product that set each level.

function RollupSummary({ data, onShowJson }) {
  const summary = data.summary;
  const counts = data.counts || {};
  const sectors = sortedSectors(summary?.sectors);

  return (
    <div style={{
      padding: "10px 12px", marginBottom: 12,
      background: "var(--bg-secondary)", border: "1px solid var(--border)",
      borderRadius: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>
          CVE prevalence:
        </span>
        {summary?.global ? (
          <>
            <LevelBadge level={summary.global} />
            {summary.global_driver && (
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                driven by <strong style={{ color: "var(--text-secondary)" }}>
                  {driverName(summary.global_driver)}
                </strong>
              </span>
            )}
          </>
        ) : (
          <span className="jobs-muted" style={{ fontSize: 12 }}>
            no product assessed yet
          </span>
        )}
        <span style={{
          marginLeft: "auto", fontSize: 11, color: "var(--text-muted)",
          fontFamily: "monospace",
        }}>
          {data.source === "live"
            ? "live master-list read"
            : `snapshot ${data.snapshot_at ? fmtTs(data.snapshot_at) : ""}`}
        </span>
        <button
          className="raw-json-link"
          onClick={onShowJson}
          title="Show the raw prevalence record — every product, sector rationale and match score behind this summary"
        >
          JSON
        </button>
      </div>

      {sectors.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
          {sectors.map(([slug, entry]) => {
            const level = entry?.level ?? entry;
            const name = CISA_SECTORS[slug] || slug;
            return (
              <span
                key={slug}
                className={`products-badge products-badge-sm products-badge-${level} products-tooltip`}
                data-tooltip={`${name}: ${level}${entry?.driver ? ` — ${driverName(entry.driver)}` : ""}`}
              >
                <span aria-hidden="true">{SECTOR_EMOJI[slug]} </span>
                {name} · {level}
              </span>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)" }}>
        {counts.scored ?? 0} of {data.affected_total ?? 0} affected product
        {data.affected_total === 1 ? "" : "s"} assessed
        {counts.unresolved ? ` · ${counts.unresolved} unresolved` : ""}
        {counts.unscored ? ` · ${counts.unscored} matched but unscored` : ""}
        {data.truncated ? " · list truncated" : ""}
      </div>
    </div>
  );
}

// ── Per-product table ────────────────────────────────────────────────────

export default function PrevalencePanel({ data, loading, error, cve }) {
  const [showJson, setShowJson] = useState(false);

  if (loading) {
    return <div style={{ padding: 8, color: "var(--text-muted)" }}>Loading prevalence...</div>;
  }
  if (error) {
    return <div style={{ padding: 8, color: "#f85149" }}>{error}</div>;
  }
  if (!data) return null;

  const products = data.products || [];
  if (products.length === 0) {
    return (
      <div style={{ padding: 8, color: "var(--text-muted)" }}>
        This CVE names no affected products that prevalence can be assessed against.{" "}
        <button className="raw-json-link" onClick={() => setShowJson(true)}>JSON</button>
        {showJson && (
          <JsonModal data={data} cve={cve} onClose={() => setShowJson(false)} />
        )}
      </div>
    );
  }

  return (
    <div>
      {showJson && (
        <JsonModal data={data} cve={cve} onClose={() => setShowJson(false)} />
      )}
      <RollupSummary data={data} onShowJson={() => setShowJson(true)} />

      <table className="products-table">
        <thead>
          <tr>
            <th>Vendor</th>
            <th>Product</th>
            <th>Global</th>
            <th>Sectors</th>
            <th>Confidence</th>
            <th>Assessed</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => {
            const prevalence = p.prevalence || {};
            const match = p.match;
            const sectors = sortedSectors(prevalence.sectors);
            // The affected[] text is shown alongside the row it resolved to,
            // never replaced by it — without both, a wrong fuzzy match is
            // invisible to the reader.
            const matchedVendor = match && match.vendor !== p.vendor ? match.vendor : null;
            const matchedProduct = match && match.product !== p.product ? match.product : null;

            return (
              <tr key={`${p.vendor}::${p.product}::${i}`} className="products-row">
                <td className="products-vendor">
                  {p.vendor || <span className="products-novendor">not stated</span>}
                  {matchedVendor && <div className="products-canonical">→ {matchedVendor}</div>}
                </td>
                <td className="products-product">
                  {p.product}
                  {matchedProduct && <div className="products-canonical">→ {matchedProduct}</div>}
                  {match && (
                    <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                      {match.match_type}
                      {typeof match.score === "number" ? ` ${match.score.toFixed(0)}%` : ""}
                    </div>
                  )}
                </td>
                <td>
                  {!match ? (
                    <span className="jobs-muted">unresolved</span>
                  ) : (
                    <LevelBadge
                      level={prevalence.global}
                      tooltip={prevalence.rationale || "No rationale recorded"}
                    />
                  )}
                </td>
                <td className="products-sectors">
                  {sectors.length === 0 ? (
                    <span className="jobs-muted">--</span>
                  ) : (
                    sectors.map(([slug, entry]) => {
                      // Older scored rows store a bare level string; newer
                      // ones store {level, rationale}.
                      const isObj = entry && typeof entry === "object";
                      const level = isObj ? entry.level : entry;
                      const name = CISA_SECTORS[slug] || slug;
                      return (
                        <span
                          key={slug}
                          className={`products-badge products-badge-sm products-badge-${level} products-tooltip`}
                          data-tooltip={isObj && entry.rationale ? `${name}: ${entry.rationale}` : name}
                        >
                          <span aria-hidden="true">{SECTOR_EMOJI[slug]} </span>
                          {name.split(" ")[0]}
                        </span>
                      );
                    })
                  )}
                </td>
                <td>{prevalence.confidence || <span className="jobs-muted">--</span>}</td>
                <td className="jobs-time" title={prevalence.assessed_at || ""}>
                  {prevalence.assessed_at ? fmtTs(prevalence.assessed_at) : "--"}
                  {prevalence.model && (
                    <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{prevalence.model}</div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
